import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './lib/supabase';
import { setStoredToken, getStoredToken } from './hooks/useSupabase';
import { Toaster } from 'react-hot-toast';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    // Force scroll reset across all mobile browsers / WebKit quirks
    try { window.scrollTo(0, 0); } catch (_) {}
    try { window.scrollTo({ top: 0, left: 0, behavior: 'instant' }); } catch (_) {}
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    // For iOS Safari where scroll may sit on a wrapper
    const root = document.getElementById('root');
    if (root) root.scrollTop = 0;
  }, [pathname]);
  return null;
};

// Test Supabase connection on load
console.log('🔌 Supabase client initialized:', supabase ? '✅' : '❌');
supabase.from('donors').select('count').then(({ data, error }) => {
  console.log('🔌 Supabase connection test:', error ? `❌ Error: ${error.message}` : '✅ Connected');
});
import { useDocument, getAdminByEmail, updateAdminLastLogin, getDonorByAuthUid } from './hooks/useSupabase';
import { isSuperAdmin } from './config/admin';
import { notificationService } from './services/notificationService';

// Import components
import Navigation from './components/Navigation';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import RegisterPage from './pages/RegisterPage';
import DonorRegistration from './components/DonorRegistration';
import RegisterDonorPage from './pages/RegisterDonorPage';
import BloodRequestForm from './components/BloodRequestForm';
import DonorProfile from './components/DonorProfile';
import DonorProfileView from './components/DonorProfileView';
import BloodRequestsDashboard from './components/BloodRequestsDashboard';
import MyBloodRequests from './components/MyBloodRequests';
import AdminDashboard from './components/AdminDashboardNew';
import LeaderboardStars from './components/LeaderboardStars';
import PWAInstall from './components/PWAInstall';
import Footer from './components/Footer';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import RequestViewPage from './pages/RequestViewPage';
import AdminDiagnostic from './components/AdminDiagnostic';

const MainWrapper = ({ children }) => {
  const location = useLocation();
  return <main className={location.pathname === '/admin' ? '' : 'pt-16'}>{children}</main>;
};

function App() {
  const [currentDonorId, setCurrentDonorId] = useState(localStorage.getItem('currentDonorId'));
  const [adminUser, setAdminUser] = useState(null);
  const [promptInstall, setPromptInstall] = useState(null);
  
  const { data: currentDonor } = useDocument('donors', currentDonorId);

  const persistDonorSession = useCallback((donorId) => {
    if (donorId) {
      setCurrentDonorId(donorId);
      localStorage.setItem('currentDonorId', donorId);
    } else {
      setCurrentDonorId(null);
      localStorage.removeItem('currentDonorId');
    }
  }, []);

  const handleDonorLogin = useCallback(async (authUser) => {
    const uid = authUser?.id || authUser?.uid;
    if (!uid) return null;
    try {
      const donor = await getDonorByAuthUid(uid);
      if (donor?.id) {
        persistDonorSession(donor.id);
      }
      return donor;
    } catch (error) {
      console.error('Error linking donor session:', error);
      return null;
    }
  }, [persistDonorSession]);

  // PWA install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setPromptInstall(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Initialize notification service
  useEffect(() => {
    if (currentDonorId && currentDonor) {
      // Setup message listener for foreground notifications
      notificationService.setupMessageListener();
      
      // Optionally register donor for notifications
      notificationService.registerDonor(currentDonorId, currentDonor)
        .catch(err => console.log('Notification registration skipped:', err.message));
    }
  }, [currentDonorId, currentDonor]);

  // Handle PWA auto-update and controller change
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      let refreshing = false;
      const hasController = !!navigator.serviceWorker.controller;
      const handleControllerChange = () => {
        if (refreshing) return;
        if (hasController) {
          refreshing = true;
          console.log('[PWA] New service worker version activated. Reloading page...');
          window.location.reload();
        }
      };
      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
      return () => {
        navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      };
    }
  }, []);

  useEffect(() => {
    // Restore auth token from localStorage on load
    const storedToken = getStoredToken();
    if (storedToken && storedToken !== import.meta.env.VITE_SUPABASE_ANON_KEY) {
      console.log('[App] Restoring auth token from localStorage');
      setStoredToken(storedToken); // Ensure it's set
    }

    // Restore admin state from localStorage on load
    const storedAdminEmail = localStorage.getItem('adminEmail');
    if (storedAdminEmail) {
      const email = storedAdminEmail.toLowerCase();
      if (isSuperAdmin(email)) {
        setAdminUser({ email, role: 'SUPER_ADMIN', is_active: true });
      } else {
        getAdminByEmail(email).then(admin => {
          if (admin) setAdminUser(admin);
          else localStorage.removeItem('adminEmail');
        }).catch(() => localStorage.removeItem('adminEmail'));
      }
    }

    // Check initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) handleAuthUser(session.user);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        await handleAuthUser(session.user);
      } else if (event === 'SIGNED_OUT') {
        // Only clear donor session on explicit sign-out, not on initial load
        // Donors using email+phone login don't have a Supabase auth session
        persistDonorSession(null);
        setAdminUser(null);
        localStorage.removeItem('adminEmail');
      }
    });

    return () => subscription.unsubscribe();
  }, [handleDonorLogin, persistDonorSession]);

  const handleAuthUser = async (user) => {
    await handleDonorLogin(user);
    if (user.email) {
      const email = user.email.toLowerCase();
      if (isSuperAdmin(email)) {
        setAdminUser({ email, role: 'SUPER_ADMIN', isActive: true });
        localStorage.setItem('adminEmail', email);
      } else {
        try {
          const admin = await getAdminByEmail(email);
          if (admin) {
            setAdminUser(admin);
            localStorage.setItem('adminEmail', email);
            await updateAdminLastLogin(admin.id);
          } else {
            setAdminUser(null);
            localStorage.removeItem('adminEmail');
          }
        } catch (error) {
          console.error('Auth listener admin lookup failed:', error);
        }
      }
    }
  };

  const handleDonorRegistered = (donorId) => {
    persistDonorSession(donorId);
  };

  const handleAdminLogin = async (email, adminData = null) => {
    try {
      const normalizedEmail = email.toLowerCase().trim();
      if (isSuperAdmin(normalizedEmail)) {
        setAdminUser({ email: normalizedEmail, role: 'SUPER_ADMIN', is_active: true });
        localStorage.setItem('adminEmail', normalizedEmail);
        return true;
      }

      // If adminData is provided (temp password flow), use it directly
      if (adminData) {
        setAdminUser(adminData);
        localStorage.setItem('adminEmail', normalizedEmail);
        return true;
      }

      const admin = await getAdminByEmail(normalizedEmail);
      if (admin) {
        setAdminUser(admin);
        localStorage.setItem('adminEmail', normalizedEmail);
        try { await updateAdminLastLogin(admin.id); } catch (_) {}
        return true;
      }

      return false;
    } catch (error) {
      console.error('[handleAdminLogin] Error:', error);
      return false;
    }
  };

  const handleDonorLoginSuccess = useCallback(async (authUserOrDonor) => {
    // If passed a full donor object directly (email+phone login), persist immediately
    if (authUserOrDonor?.id && !authUserOrDonor?.aud) {
      persistDonorSession(authUserOrDonor.id);
      return true;
    }
    // Otherwise it's a Supabase auth user — look up the linked donor
    const donor = await handleDonorLogin(authUserOrDonor);
    return Boolean(donor);
  }, [handleDonorLogin, persistDonorSession]);

  const handleLogout = () => {
    supabase.auth.signOut().catch((err) => console.warn('Sign out error:', err?.message));
    persistDonorSession(null);
    setAdminUser(null);
    localStorage.removeItem('adminEmail');
  };

  return (
    <div className="bg-surface-50 min-h-screen">
      <Router>
        <Toaster 
          position="top-center" 
          reverseOrder={false}
          toastOptions={{
            style: { fontFamily: 'Inter, system-ui, sans-serif', borderRadius: '12px', fontSize: '14px' },
            success: { iconTheme: { primary: '#22c55e', secondary: '#fff' } },
            error:   { iconTheme: { primary: '#e11d48', secondary: '#fff' } },
          }}
        />
        <ScrollToTop />
        <Navigation 
          currentDonor={currentDonor} 
          adminUser={adminUser}
          onLogout={handleLogout}
        />
        <MainWrapper>
          <Routes>
            <Route path="/" element={<HomePage currentDonor={currentDonor} adminUser={adminUser} />} />
            <Route 
              path="/login" 
              element={<LoginPage onAdminLogin={handleAdminLogin} onDonorLogin={handleDonorLoginSuccess} />} 
            />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/register-donor" element={<RegisterDonorPage onSuccess={handleDonorRegistered} />} />
            <Route path="/donor-registration" element={<RegisterDonorPage onSuccess={handleDonorRegistered} />} />
            <Route path="/request-blood" element={<BloodRequestForm requestedBy={currentDonorId} />} />
            <Route path="/profile" element={<DonorProfile donor={currentDonor} onUpdate={(updatedDonor) => setCurrentDonorId(updatedDonor.id)} />} />
            <Route path="/donor-profile" element={<DonorProfileView />} />
            <Route path="/requests" element={<BloodRequestsDashboard currentDonor={currentDonor} />} />
            <Route path="/requests/:requestId" element={<RequestViewPage />} />
            <Route path="/my-requests" element={<MyBloodRequests requestedBy={currentDonorId} />} />
            <Route path="/admin" element={<AdminDashboard currentUser={adminUser} onLogout={handleLogout} />} />
            <Route path="/stars" element={<LeaderboardStars />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/admin-diagnostic" element={<AdminDiagnostic />} />
            <Route path="/request/:requestId" element={<RequestViewPage />} />
          </Routes>
        </MainWrapper>
        <Footer />
        <PWAInstall promptInstall={promptInstall} />
      </Router>
    </div>
  );
}

export default App;